import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PasswordInput } from '@/components/auth/PasswordInput';
import { loginSchema } from '@/lib/validations';
import { useAuthStore } from '@/store/authStore';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    clearError();

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string') {
          errors[key] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const loginResult = await login({ email, password });
    if (loginResult.ok) {
      void navigate('/job-postings', { replace: true });
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-brand-pale px-4'>
      <div className='w-full max-w-md'>
        <div className='bg-white rounded-lg shadow-lg p-8'>
          <div className='text-center mb-8'>
            <h1 className='text-2xl font-bold text-brand-gray'>{t('login.title')}</h1>
            <p className='text-gray-600 mt-2'>{t('login.subtitle')}</p>
          </div>

          {error && (
            <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
              <p className='text-sm text-red-700'>{error}</p>
            </div>
          )}

          <form onSubmit={(e) => void handleSubmit(e)} className='space-y-5'>
            <div>
              <label htmlFor='email' className='block text-sm font-medium text-gray-700 mb-1'>
                {t('login.email')}
              </label>
              <input
                id='email'
                type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                disabled={isLoading}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  fieldErrors['email'] ? 'border-red-500' : 'border-gray-300'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              {fieldErrors['email'] && <p className='mt-1 text-sm text-red-600'>{fieldErrors['email']}</p>}
            </div>

            <div>
              <label htmlFor='password' className='block text-sm font-medium text-gray-700 mb-1'>
                {t('login.password')}
              </label>
              <PasswordInput
                id='password'
                value={password}
                onChange={setPassword}
                placeholder={t('login.passwordPlaceholder')}
                disabled={isLoading}
                hasError={!!fieldErrors['password']}
              />
              {fieldErrors['password'] && <p className='mt-1 text-sm text-red-600'>{fieldErrors['password']}</p>}
            </div>

            <button
              type='submit'
              disabled={isLoading}
              className='w-full py-3 bg-primary text-white font-bold rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed'>
              {isLoading ? t('login.submitting') : t('login.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
